<template>
  <div>
    <p class="sr-only">Tutti i campi sono obbligatori</p>
    <form>
      <ValidationObserver
        v-slot="{ errors }"
        ref="observer"
        tag="div"
        :slim="true"
      >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-9.6 md:gap-12">
          <InputWithValidation
            :value="first_name"
            name="Nome"
            label="Nome"
            placeholder="Nome"
            rules="required|alpha_spaces"
            :errors="errors"
            :field-classes="errors.Nome?.length ? 'mb-10' : 'mb-0'"
            @input="onFormFieldEvent($event, 'first_name', 'name')"
          />
          <InputWithValidation
            :value="last_name"
            name="Cognome"
            label="Cognome"
            placeholder="Cognome"
            rules="required|alpha_spaces"
            :errors="errors"
            :field-classes="errors.Cognome?.length ? 'mb-10' : 'mb-0'"
            @input="onFormFieldEvent($event, 'last_name', 'surname')"
          />

          <DropdownWithValidation
            :value="state"
            :options="states"
            name="IDProvincia"
            label="Provincia"
            placeholder="Provincia"
            rules="required"
            :errors="errors"
            :field-classes="errors.IDProvincia?.length ? 'mb-10' : 'mb-0'"
            @focus="onTealiumFormFirstFieldFocus"
            @select="onSelectState($event)"
          />
          <DropdownWithValidation
            :value="city"
            :options="cities"
            name="IDComune"
            label="Comune"
            placeholder="Comune"
            rules="required"
            :errors="errors"
            :field-classes="errors.IDComune?.length ? 'mb-10' : 'mb-0'"
            @select="onFormFieldEvent($event, 'city', 'IDComuneRicerca')"
          />
          <InputWithValidation
            :value="email"
            name="email"
            label="Email"
            placeholder="Email"
            rules="required|email"
            :errors="errors"
            :field-classes="errors.email?.length ? 'mb-10' : 'mb-0'"
            @input="onFormFieldEvent($event, 'email')"
          />
          <InputWithValidation
            :value="phone"
            type="number"
            name="Telefono"
            label="Telefono"
            placeholder="Telefono"
            rules="required|phone"
            :errors="errors"
            :field-classes="errors.Telefono?.length ? 'mb-10' : 'mb-0'"
            @input="onFormFieldEvent($event, 'phone')"
          />
        </div>

        <div class="mb-4 mt-10">
          <h3 class="font-bold mb-2">Imposta password</h3>
          <p class="text-sm mb-2">
            La password deve essere sicura e includere
            <strong>
              almeno una lettera maiuscola, una minuscola, un numero e un
              simbolo, con minimo 14 caratteri.
            </strong>
            Evita sequenze, ripetizioni o password già utilizzate.
          </p>
          <p class="text-sm mb-4">
            <strong>Ricorda</strong>: la password scade ogni 6 mesi, quindi
            scegli una combinazione unica e facile da ricordare.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-9.6 md:gap-12">
          <!-- NOTA: Aggiornata regola min:14 come da V3 -->
          <InputWithValidation
            ref="password"
            :value="password"
            type="password"
            password-reveal
            name="password"
            label="Password"
            placeholder="Lunghezza minima ≥ 14 caratteri"
            rules="required|min:14|strongPassword"
            :errors="errors"
            :field-classes="errors.password?.length ? 'mb-10' : 'mb-0'"
            @input="onFormFieldEvent($event, 'password')"
          />
          <InputWithValidation
            :value="passwordConfirmation"
            type="password"
            password-reveal
            name="password_confirmation"
            label="Conferma Password"
            placeholder="Lunghezza minima ≥ 14 caratteri"
            rules="required|confirmed:password"
            :errors="errors"
            :field-classes="
              errors.password_confirmation?.length ? 'mb-10' : 'mb-0'
            "
            @input="
              onFormFieldEvent(
                $event,
                'passwordConfirmation',
                'password_confirmation'
              )
            "
          />
        </div>

        <p class="mb-4 mt-10">Sono interessato a:</p>
        <RadioWithValidation
          :items="contactTypes"
          :value="contactType"
          name="TipoContatto"
          rules="required"
          field-classes=" mb-9.6 md:mb-12"
          :errors="errors"
          @input="contactType = $event"
        >
        </RadioWithValidation>

        <PrivacyCheckbox
          v-model="privacy"
          :errors="errors"
          policy="registration"
          class="my-9.6 md:mb-12"
        />
      </ValidationObserver>
      <p class="text-right">
        <UcrsButton
          type="primary"
          class="w-100"
          :disabled="isDisabled"
          @click.native.prevent="onSubmit"
        >
          Registrati ora
        </UcrsButton>
      </p>
      <ul v-if="resErrors.length" class="text-red text-right mt-4">
        <li v-for="(err, i) in resErrors" :key="i" v-text="err"></li>
      </ul>
    </form>
  </div>
</template>

<script>
// Mixins
import FormMixin from '~/mixins/form'

// Components
import UcrsButton from '~/components/UcrsButton.vue'
import DropdownWithValidation from '~/components/DropdownWithValidation.vue'
import InputWithValidation from '~/components/InputWithValidation.vue'
import PrivacyCheckbox from '~/components/PrivacyCheckbox.vue'
import RadioWithValidation from '~/components/RadioWithValidation.vue'

export default {
  components: {
    UcrsButton,
    DropdownWithValidation,
    InputWithValidation,
    PrivacyCheckbox,
    RadioWithValidation,
  },

  mixins: [FormMixin],

  data() {
    return {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      contactType: 'c',
      contactTypes: [
        { label: 'Comprare casa', value: 'c' },
        { label: 'Vendere casa', value: 'v' },
      ],
      password: '',
      passwordConfirmation: '',
      states: [],
      state: null,
      city: null,
      cities: [],
      privacy: false,
      isDisabled: false,
      resErrors: [],
    }
  },

  async created() {
    // Caricamento stati (province) - endpoint esistente V2 o V1 ok se non cambiato
    const statesRes = await this.$axios.get('/_api/api/v2/geo/states')
    this.states = statesRes.data.data
  },

  mounted() {
    const savedSearch = this.$store.state.interactions.search
    if (!savedSearch) {
      return
    }
    const { email } = savedSearch
    this.email = email
  },

  methods: {
    async onSelectState(selected) {
      this.cities = []
      this.city = ''
      this.state = selected
      if (!this.state) return
      const res = await this.$axios.get(
        `/_api/api/v2/geo/states/${this.state.id}`,
        {
          params: {
            embed: 'cities',
          },
        }
      )
      this.cities = res.data.data.cities

      this.tealiumFormUpdateLastField('IDProvinciaRicerca')
    },

    async onSubmit() {
      const validation = await this.validate()
      if (!validation.isValid) {
        const tealiumErrors = validation.errorFields.map((el) => {
          return { campo: el }
        })
        this.$tealium.track({
          event_name: 'errori_richiesta',
          errori_richiesta: tealiumErrors,
        })
        return
      }

      this.isDisabled = true
      try {
        // Mappatura payload in base a RegisterRequest del backend V3
        const data = {
          email: this.email,
          password: this.password,
          password_confirmation: this.passwordConfirmation,
          first_name: this.first_name,
          last_name: this.last_name,
          phone: this.phone,
          type: this.contactType,
          // Inviamo l'ID come stringa o int, RegisterRequest lo mapperà a IDProvinciaRicerca
          state: this.state ? this.state.id : null,
          city: this.city ? this.city.id : null,
          privacy: this.privacy,
        }

        await this.$axios.get('/_api/sanctum/csrf-cookie')

        // Chiamata endpoint V3
        await this.$axios.post(`/_api/api/v3/auth/register`, data)

        // NOTA: Con la V3 non c'è più login automatico.
        // Emettiamo success con messaggio per invitare l'utente a controllare la mail.
        this.$emit('success', {
          message:
            "Registrazione avvenuta con successo. Controlla la tua email per confermare l'account e procedi al login.",
        })
      } catch (error) {
        this.resErrors = this.$utilities.responseErrorsToArray(error.response)
      } finally {
        this.isDisabled = false
      }
    },

    async validate() {
      await this.$refs.observer.validate()
      const fields = []
      if (this.$refs.observer.flags.invalid) {
        const errors = this.$refs.observer.errors
        for (const property in errors) {
          if (errors[property][0]) fields.push(property)
        }
      }
      return { isValid: this.$refs.observer.flags.valid, errorFields: fields }
    },
  },
}
</script>

<style lang="postcss">
@import '~/assets/css/components/field';
@import '~/assets/css/components/button';
@import '~/assets/css/components/input';
</style>
